import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { convertStructureUnitsToLocations, filterStructureUnitsByLegacyType } from "@/composables/spanBySpan";
import { useInspectionStore } from "@/stores/inspection";
import { STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";
import { SUBSTRUCTURE_DATA } from "@/constants/Substructure";
import { clone } from "@/util/clone";

export const useSubstructureStore = defineStore("substructure", () => {
  //data is loaded either from idb(pouch db for inspection module) or service(for management module)
  let pageData = ref(clone(SUBSTRUCTURE_DATA));
  let pageDataOriginal = ref(clone(SUBSTRUCTURE_DATA));
  const inspectionStore = useInspectionStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_SUBSTRUCTURE;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Substructure", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspLinkObject = (waterway) => {
    return {
      // identifiers
      brkey: waterway.brkey,
      inspectionId: waterway.inspectionId,

      // inspection
      subRating: waterway.subRating,
      scourCriticalCat: waterway.scourCriticalCat,
      scourCritical: waterway.scourCritical,

      // scourComp
      scbiSource: waterway.scbiSource,
      overallOSA: waterway.overallOSA,

      // userInspection
      scourVulnerability: waterway.scourVulnerability,

      // inspectionConditionHist
      subRatingDuration: waterway.subRatingDuration,

      // comments
      inspectionComments: waterway.inspectionComments,
      structureUnitInspComments: waterway.structureUnitInspComments,

      // page values
      structureUnits: waterway.structureUnits,
      inspectionsUnderwater: waterway.inspectionsUnderwater,
      inspectionFcs: waterway.inspectionFCs,
    };
  };

  const convertToSaveInspModuleObject = (waterwayValue, structure) => {
    // inspection
    structure.inspection.subRating = waterwayValue.subRating;
    structure.inspection.scourCriticalCat = waterwayValue.scourCriticalCat;
    structure.inspection.scourCritical = waterwayValue.scourCritical;

    // scourComp
    structure.scourComp.scbiSource = waterwayValue.scbiSource;
    structure.scourComp.overallOSA = waterwayValue.overallOSA;
    // userInspection
    structure.userInspection.scourVulnerability = waterwayValue.scourVulnerability;
    // inspectionConditionHist
    structure.inspectionConditionHist.subRatingDuration = waterwayValue.subRatingDuration;
    structure.inspectionConditionHist.subRatingDurationChanges = waterwayValue.subRatingDurationChanges;

    structure.structureUnits = waterwayValue.structureUnits;
    structure.structureUnitInspComments = waterwayValue.structureUnitInspComments;
    structure.inspectionsUnderwater = waterwayValue.inspectionsUnderwater;
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      brkey: structure?.structureBridge?.brkey,
      inspectionId: structure?.inspection?.inspectionId,

      subRating: structure?.inspection?.subRating,
      scourCriticalCat: structure?.inspection?.scourCriticalCat,
      scourCritical: structure?.inspection?.scourCritical,

      scourVulnerability: structure?.userInspection?.scourVulnerability,

      subRatingDuration: structure?.inspectionConditionHist?.subRatingDuration,
      subRatingDurationChanges: structure?.inspectionConditionHist?.subRatingDurationChanges,

      deptMainStrucConfig: structure?.userBridge?.deptMainStrucConfig,

      scourCalculated: structure?.scourComp?.scourCalculated,
      scbiSource: structure?.scourComp?.scbiSource,
      overallOSA: structure?.scourComp?.overallOSA,

      structureUnits: structure?.structureUnits,
      inspectionsUnderwater: structure?.inspectionsUnderwater,
      structureUnitInspComments: structure?.structureUnitInspComments,
      inspectionFCs: structure?.inspectionFCs,
      locations: convertStructureUnitsToLocations(
        inspectionStore.getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.PIER, STRUCTURE_UNIT_TYPE.ABUTMENT], true)
      ),
    };
    pageDataOriginal.value = clone(pageData.value);
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    let url = INSPECTION_LINKS_ENDPOINT.GET_SUBSTRUCTURE.replace("{brkey}", brkey).replace(
      "{inspectionId}",
      inspectionId
    );

    await axios
      .get(url)
      .then((resp) => {
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const getSubstructureCommentIndex = (structureUnit, commentType) => {
    return pageData.value?.structureUnitInspComments.findIndex(
      (element) => element?.structureUnitId == structureUnit?.structureUnitId && element?.commentType == commentType
    );
  };

  const getStructureUnitFromLabel = (unitLabel) => {
    return pageData.value?.structureUnits?.find((unit) => unit.strUnitLabel == unitLabel);
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,

      // userBridge
      deptMainStrucConfig: response.deptMainStrucConfig,

      // scourComp
      scourCalculated: response.scourCalculated,
      scbiSource: response.scbiSource,
      overallOSA: response.overallOSA,

      // inspection
      subRating: response.subRating,
      scourCriticalCat: response.scourCriticalCat,
      scourCritical: response.scourCritical,

      // userInspection
      scourVulnerability: response.scourVulnerability,

      // inspectionConditionHist
      subRatingDuration: response.subRatingDuration,
      subRatingDurationChanges: "",

      // comments
      inspectionComments: response.inspectionComments,
      structureUnitInspComments: response.structureUnitInspComments,

      // page values
      structureUnits: response.structureUnits,
      inspectionsUnderwater: response.inspectionsUnderwater,
      inspectionFCs: response.inspectionFcs,
      locations: convertStructureUnitsToLocations(
        filterStructureUnitsByLegacyType(
          response.structureUnits,
          [STRUCTURE_UNIT_TYPE.PIER, STRUCTURE_UNIT_TYPE.ABUTMENT],
          true
        )
      ),
    };
    pageDataOriginal.value = clone(pageData.value);
  };

  const resetPageData = () => {
    pageData.value = clone(pageDataOriginal.value);
  };

  const clearPageData = () => {
    pageData.value = clone(SUBSTRUCTURE_DATA);
  };

  const getSubStructureUnitKey = (structure, structureType) => {
    return structure?.structureUnits?.find((unit) => unit.strUnitLabel == structureType)?.strUnitKey;
  };

  const getUnderwaterInspForPier = (structure, StructureUnitKey) => {
    return structure?.inspectionsUnderwater?.find((unit) => unit.strUnitKey == StructureUnitKey);
  };

  const getPiers = () => {
    let returnValue = pageData.value?.structureUnits?.filter((unit) => unit.legacyStructureType == "P");
    returnValue?.sort(function (a, b) {
      let splitA = a.strUnitLabel.split("P");
      let splitB = b.strUnitLabel.split("P");
      let aPlaceholder;
      let bPlaceholder;
      if (splitA[1].charAt(0) == "0") {
        aPlaceholder = Number.parseInt(splitA[1].charAt(1));
      } else {
        aPlaceholder = Number.parseInt(splitA[1]);
      }
      if (splitB[1].charAt(0) == "0") {
        bPlaceholder = Number.parseInt(splitB[1].charAt(1));
      } else {
        bPlaceholder = Number.parseInt(splitB[1]);
      }
      return aPlaceholder - bPlaceholder;
    });

    return returnValue || [];
  };

  return {
    clearPageData,
    getStructureUnitFromLabel,
    getSubstructureCommentIndex,
    pageDataOriginal,
    resetPageData,
    getPiers,
    getUnderwaterInspForPier,
    getSubStructureUnitKey,
    pageData,
    saveData,
    loadDataObject,
  };
});
